import { ref, computed, onMounted, onUnmounted } from 'vue'
import { BREAKPOINTS } from '@/constants/breakpoints'

/**
 * Composable for handling responsive behavior
 * Provides reactive screen size detection and breakpoint checks
 * Breakpoints match Tailwind config: sm(640), md(768), lg(1024), xl(1280), 2xl(1536)
 */
export const useResponsive = () => {
  const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
  const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 768)

  let rafId = null
  const handleResize = () => {
    if (rafId) return
    rafId = requestAnimationFrame(() => {
      windowWidth.value = window.innerWidth
      windowHeight.value = window.innerHeight
      rafId = null
    })
  }

  const isMobile = computed(() => windowWidth.value < BREAKPOINTS.LG)
  const isTablet = computed(() => windowWidth.value >= BREAKPOINTS.MD && windowWidth.value < BREAKPOINTS.LG)
  const isDesktop = computed(() => windowWidth.value >= BREAKPOINTS.LG)
  const isLargeDesktop = computed(() => windowWidth.value >= BREAKPOINTS.XL)
  const isExtraLarge = computed(() => windowWidth.value >= BREAKPOINTS.XXL)

  const isMobileDevice = computed(() => {
    if (typeof navigator === 'undefined') return false
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  })

  onMounted(() => {
    window.addEventListener('resize', handleResize)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    if (rafId) cancelAnimationFrame(rafId)
  })

  return {
    windowWidth,
    windowHeight,
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    isExtraLarge,
    isMobileDevice,
    BREAKPOINTS,
  }
}
